export const DEFAULT_SEGMENT_THICKNESS = 0.2;

/** XZ düzlem noktası (y=0) */
export interface XzPoint {
  x: number;
  z: number;
}

/**
 * Tek duvar merkez ekseni. Her segmentin start/end noktası vardır;
 * control mevcutsa eksen quadratic Bezier, yoksa düz line'dır.
 */
export interface SegmentPath {
  start: XzPoint;
  end: XzPoint;
  control?: XzPoint;
}

/** Tek segment tanımı — source of truth (document / history) */
export interface SegmentSpec {
  id: string;
  /** Toplam kalınlık = upThickness + downThickness */
  thickness: number;
  /** Eksen +normal (logical e1) tarafı offset */
  upThickness: number;
  /** Eksen -normal (logical e3) tarafı offset */
  downThickness: number;
  path: SegmentPath;
  /** Başka bir segment path'i üzerinde kullanılan segment. */
  placement?: SegmentPlacement;
  /** Geometriden bağımsız görsel kullanım. */
  appearance?: SegmentAppearance;
}

export interface SegmentPlacement {
  hostSegmentId: string;
  startT: number;
  endT: number;
  effect: "overlay" | "cut";
  /** 3D cut aralığı; yalnızca effect=cut için anlamlıdır. */
  bottom?: number;
  height?: number;
}

/** Door leaf hinge along the opening path. */
export type DoorHingeSide = "start" | "end";
/** Door swings into the room ("in") or toward exterior ("out"). */
export type DoorOpensSide = "in" | "out";

export interface SegmentAppearance {
  rendererId?: string;
  fillColor?: number;
  wireColor?: number;
  /**
   * Legacy BlankWall: keep floor boundary / graph, but no 3D wall extrusion.
   * Used by open / wall-less room presets.
   */
  invisibleIn3d?: boolean;
  /** Door only — which path endpoint is the hinge. Default `"start"`. */
  doorHinge?: DoorHingeSide;
  /** Door only — swing into interior or exterior. Default `"in"`. */
  doorOpens?: DoorOpensSide;
  /**
   * Door only — arc sign in plan (+1 = +π/2 / e3, -1 = −π/2 / e1).
   * Cached from `doorOpens` + host interior; renderer reads this.
   */
  doorSwingSign?: 1 | -1;
  /**
   * Door only — wall face the leaf pivot sits on (1 = e1/up, 3 = e3/down).
   * `in` → interior face; `out` → exterior face.
   */
  doorPivotLogicalEdge?: 1 | 3;
  /** Door only — open angle in degrees (0 = closed, 90 = default). */
  doorOpenAngleDeg?: number;
}

export interface SegmentEdge2D {
  start: XzPoint;
  end: XzPoint;
}

/** Segment kutusu — köşeler + 4 logical kenar */
export interface SegmentGeometry {
  /** CW polygon ring; line'da 4, curve'de adaptive olarak n köşe. */
  corners: XzPoint[];
  logicalEdges: [
    SegmentEdge2D,
    SegmentEdge2D,
    SegmentEdge2D,
    SegmentEdge2D,
  ];
  /** startCap, upSide, endCap, downSide fiziksel edge zincirleri. */
  boundaryEdges: [
    SegmentEdge2D[],
    SegmentEdge2D[],
    SegmentEdge2D[],
    SegmentEdge2D[],
  ];
}

export type LogicalEdgeIndex = 0 | 1 | 2 | 3;

export interface PlanarGraphEdge {
  startVertex: number;
  endVertex: number;
  ownerSegmentId: string;
  logicalEdgeIndex: LogicalEdgeIndex;
}

export interface PlanarGraphData {
  vertices: XzPoint[];
  edges: PlanarGraphEdge[];
}

export interface FaceData {
  vertexIndices: number[];
}

/** Rebuild sonrası tek segment türevi */
export interface SegmentEntry {
  spec: SegmentSpec;
  /** Mitred outline — pick, fill, dimension ile graph wire uyumlu */
  outline: SegmentGeometry;
  /** graph.edges index'leri, logicalEdge 0–3; start→end sırası */
  fragments: [number[], number[], number[], number[]];
}

export interface PlanarGraphFaces {
  exterior: FaceData[];
  interior: FaceData[];
}

export interface PlanarGraph {
  segments: SegmentEntry[];
  graph: PlanarGraphData;
  faces: PlanarGraphFaces;
}

/** Parçalanmadan önceki logical kenar — graph edge'de ownerSegmentId + logicalEdgeIndex */
export interface LogicalEdgeRef {
  segmentId: string;
  logicalEdgeIndex: LogicalEdgeIndex;
}

export type SegmentPickTarget =
  | { kind: "logicalEdge"; ref: LogicalEdgeRef }
  | { kind: "segmentWall"; segmentId: string }
  | { kind: "interior"; faceIndex: number }
  | { kind: "centerlineEndpoint"; segmentId: string; role: "start" | "end" };
